import { html, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import type { CenterOfGravity } from '../types/morningstar.types';
import {
  getCellClassMap,
  isBoldPosition,
} from '../utils/morningstar.style.utils';
import style from './morningstar-style-box-row.scss?inline';
import { PopBaseComponent } from '../../../base/pop-base-component';

/**
 * Morningstar Style Box Row Component
 *
 * Represents a single row in the Morningstar Style Box grid.
 * Can be either a data row or a label row.
 *
 * @element pop-morningstar-style-box-row
 *
 * @property {CenterOfGravity} centerOfGravity - Position of the center of gravity
 * @property {string} rowPosition - Current row position
 * @property {string} label - Label for the row
 * @property {readonly string[]} labels - Labels for the cells
 * @property {boolean} isLabelRow - Whether this row is a label row
 * @property {string} boldRow - Row position that should be bold
 * @property {string} boldCol - Column position that should be bold
 *
 * @csspart cell - Individual cells in the row
 * @csspart label - Text labels in the row
 *
 * @private
 */
export class MorningstarStyleBoxRow extends PopBaseComponent {
  static get is(): string {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return 'pop-morningstar-style-box-row' as keyof HTMLElementTagNameMap;
  }

  static styles = [unsafeCSS(super.styles), unsafeCSS(style)];

  @property({ type: String, attribute: 'center-of-gravity', reflect: true })
  centerOfGravity: CenterOfGravity;

  @property({ type: String })
  rowPosition = '';

  @property({ type: String, reflect: true })
  label = '';

  @property({ type: Array })
  labels: string[] = [];

  @property({ type: Boolean, attribute: 'is-label-row', reflect: true })
  isLabelRow = false;

  @property({ type: String })
  boldRow = '';

  @property({ type: String })
  boldCol = '';

  render() {
    return html`
      <div class="morningstar-grid-row">
        ${this.isLabelRow ? this.renderLabelCells() : this.renderDataCells()}
        ${!this.isLabelRow ? this.renderRowLabel() : ''}
      </div>
    `;
  }

  /**
   * Renders data cells for the row
   */
  private renderDataCells() {
    return this.labels.map((col) => {
      const cellClasses = getCellClassMap('morningstar-grid-row__cell', {
        highlight: !!this.centerOfGravity && this.isHighlighted(col),
      });

      return html`<div class=${classMap(cellClasses)}></div>`;
    });
  }

  /**
   * Renders label cells for the footer row
   */
  private renderLabelCells() {
    return this.labels.map((label) => {
      const cellClasses = {
        'morningstar-grid__label': true,
        'b-font-weight-bold': isBoldPosition(
          label.toLowerCase(),
          this.boldRow,
          this.boldCol,
        ),
        'b-typography-sans-serif--caption--100': true,
      };

      return html`<div class=${classMap(cellClasses)}>${label}</div>`;
    });
  }

  /**
   * Renders the row label
   */
  private renderRowLabel() {
    const labelClasses = {
      'morningstar-grid__label': true,
      'b-padding-inline--micro--mini': true,
      'b-font-weight-bold': isBoldPosition(
        this.rowPosition,
        this.boldRow,
        this.boldCol,
      ),
      'b-typography-sans-serif--caption--100': true,
    };

    return html`<div class=${classMap(labelClasses)}>${this.label}</div>`;
  }

  /**
   * Checks if a cell should be highlighted based on center of gravity
   */
  private isHighlighted(col: string): boolean {
    return this.centerOfGravity === `${this.rowPosition}-${col.toLowerCase()}`;
  }
}

customElements.define(MorningstarStyleBoxRow.is, MorningstarStyleBoxRow);
